import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface EmailTemplateRecord {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent: string | null;
  category: string;
  description: string;
  liquidVariables: string[];
  source: "system" | "tenant";
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateEmailTemplateInput {
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string | null;
  category?: string;
}

export interface UpdateEmailTemplateInput extends CreateEmailTemplateInput {
  templateId: string;
}

export function useEmailTemplates(category?: string) {
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const query = category ? `?category=${encodeURIComponent(category)}` : "";

    try {
      const response = await apiFetch<{ data: EmailTemplateRecord[] }>(`/email-marketing/templates${query}`);
      setTemplates(response.data ?? []);
    } catch (err) {
      setTemplates([]);
      setError(err instanceof ApiError ? err.message : "Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    isLoading,
    error,
    refetch: fetchTemplates,
  };
}

export async function createEmailTemplate(
  input: CreateEmailTemplateInput,
): Promise<EmailTemplateRecord> {
  const response = await apiFetch<{ data: EmailTemplateRecord }>("/email-marketing/templates", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function updateEmailTemplate(
  input: UpdateEmailTemplateInput,
): Promise<EmailTemplateRecord> {
  const response = await apiFetch<{ data: EmailTemplateRecord }>(`/email-marketing/templates/${input.templateId}`, {
    method: "PATCH",
    body: JSON.stringify({
      name: input.name,
      subject: input.subject,
      htmlContent: input.htmlContent,
      textContent: input.textContent,
      category: input.category,
    }),
  });
  return response.data;
}

export async function deleteEmailTemplate(templateId: string): Promise<void> {
  await apiFetch(`/email-marketing/templates/${templateId}`, {
    method: "DELETE",
  });
}
